
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';

const sections = [
  {
    title: 'Information We Collect',
    content: [
      'We collect information you provide directly to us, including:',
      'Name and contact information',
      'Business name and details',
      'Payment information',
      'Communications with us',
      'Website usage data',
    ],
  },
  {
    title: 'How We Use Your Information',
    content: [
      'We use the information we collect to:',
      'Provide, maintain, and improve our services',
      'Process transactions and send related information',
      'Send you technical notices and support messages',
      'Respond to your comments, questions, and requests',
      'Communicate with you about products, services, and events',
    ],
  },
  {
    title: 'SMS/Text Messaging',
    subsections: [
      {
        subtitle: 'How We Obtain Consent',
        text: 'We obtain your express written consent to send SMS/text messages through our online opt-in form. By providing your mobile phone number and checking the consent checkbox, you agree to receive recurring automated marketing text messages from Atlas System at the phone number provided. Consent is not a condition of any purchase.',
      },
      {
        subtitle: 'How We Use Your Mobile Information',
        text: 'Your mobile phone number is used solely to send you text messages related to:',
        list: [
          'Marketing promotions and special offers',
          'Service updates and announcements',
          'Appointment reminders and confirmations',
          'Responses to your inquiries',
        ],
      },
      {
        subtitle: 'Mobile Information Sharing',
        text: 'We do not sell, rent, lease, or share your mobile phone number or any information collected through our SMS program with any third parties for their own marketing purposes. Your phone number will only be shared with service providers who help us deliver text messages (such as our messaging platform), and they are contractually prohibited from using your information for any purpose other than providing this service to us.',
      },
      {
        subtitle: 'Opting Out',
        text: 'You can opt out of receiving text messages at any time by replying STOP to any message. After opting out, you will receive a confirmation message and will no longer receive SMS messages from us. Message and data rates may apply. Reply HELP for assistance.',
      },
    ],
  },
  {
    title: 'Information Sharing',
    content: [
      'We do not sell, trade, or rent your personal information to third parties. We may share your information with:',
      'Service providers who assist in our operations',
      'Professional advisers (lawyers, accountants)',
      'Law enforcement when required by law',
    ],
  },
  {
    title: 'Data Security',
    content: [
      'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.',
    ],
  },
  {
    title: 'Cookies',
    content: [
      'We use cookies and similar tracking technologies to track activity on our website and hold certain information. You can instruct your browser to refuse all cookies or indicate when a cookie is being sent.',
    ],
  },
  {
    title: 'Third-Party Links',
    content: [
      'Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these sites.',
    ],
  },
  {
    title: 'Your Rights',
    content: [
      'You have the right to:',
      'Access your personal information',
      'Correct inaccurate information',
      'Request deletion of your information',
      'Opt-out of marketing communications',
    ],
  },
  {
    title: 'Children\'s Privacy',
    content: [
      'Our services are not intended for children under 18. We do not knowingly collect personal information from children.',
    ],
  },
  {
    title: 'Changes to This Policy',
    content: [
      'We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.',
    ],
  },
  {
    title: 'Contact Us',
    content: [
      'If you have questions about this Privacy Policy, please contact us at info@atlassystem.io',
    ],
  },
];

export default function PrivacyPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="https://static.readdy.ai/image/e31f2f4702878072217cd119276d8644/5b56961b60c36d4ff2ff86c82d13a1e4.png"
              alt="AI Receptionist Logo"
              className="h-12 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              Home
            </Link>
            <Link
              to="/about"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              About
            </Link>
            <Link
              to="/contact"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              Contact
            </Link>
            <Link
              to="/#contact"
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <article className="pt-32 pb-24 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-widest text-purple-600 uppercase mb-4">
            LEGAL
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500 mb-12">Last updated: February 18, 2026</p>

          <div className="prose prose-gray max-w-none">
            {sections.map((section, i) => (
              <div key={i} className="mb-10">
                <h4 className="text-xl font-bold text-gray-900 mb-4">
                  <a
                    href={`#section-${i}`}
                    id={`section-${i}`}
                    className="hover:text-purple-600 transition-colors"
                  >
                    {i + 1}. {section.title}
                  </a>
                </h4>
                
                {section.subsections ? (
                  <div className="space-y-6">
                    {section.subsections.map((subsection, j) => (
                      <div key={j}>
                        <p className="text-base font-semibold text-gray-800 mb-2">
                          {subsection.subtitle}:
                        </p>
                        <p className="text-sm text-gray-600 leading-relaxed mb-2">
                          {subsection.text}
                        </p>
                        {subsection.list && (
                          <ul className="space-y-2 ml-4">
                            {subsection.list.map((item, k) => (
                              <li key={k} className="flex items-start gap-3">
                                <span className="w-1.5 h-1.5 bg-purple-400 rounded-full mt-2 flex-shrink-0"></span>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                  {item}
                                </p>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {section.content.map((item, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <span className="w-1.5 h-1.5 bg-purple-400 rounded-full mt-2 flex-shrink-0"></span>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {item}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
